/**
 * Groupes de rôles legacy centralisés.
 * Utiliser ces constantes pour tous les filtres DB et JS — jamais de chaîne
 * codée en dur dans le code métier.
 *
 * Ces groupes reflètent le mapping LEGACY_TO_SYSTEM de useGovAccess.js.
 * En mode dynamic RBAC, les droits proviennent des user_role_assignments ;
 * ces constantes servent uniquement à identifier le "type" d'utilisateur
 * pour les listes et statistiques (pas pour la sécurité).
 */

/** Identifiants de rôles plateforme — seule source de vérité pour les checks super-admin. */
const MASTER_ROLE = 'Master';
// Conservé comme LIBELLÉ historique (affichage, migration B) mais RETIRÉ des
// rôles plateforme : « IT » est désormais un rôle tenant (it_admin), pas un
// administrateur de plateforme. P0 sous-lot E, 2026-07-23.
export const IT_ROLE = 'IT';

/**
 * Rôles opérés par Rectorium SaaS (sans tenant_id).
 * Utilisés pour les checks cross-tenant et la protection du portail Master.
 *
 * P0 sous-lot E : `IT` retiré. Un administrateur informatique d'établissement
 * n'est PAS un administrateur de plateforme. Seul `Master` (résolu par
 * l'autorité canonique côté serveur) confère le pouvoir plateforme.
 */
export const PLATFORM_ADMIN_ROLES = [MASTER_ROLE];

/**
 * Libellés legacy résolvant tous sur le rôle système 'dg' (LEGACY_TO_SYSTEM,
 * useGovAccess.js). Utilisé pour MASQUER des cockpits role-native du menu du
 * DG/Recteur (demande Alfred, 2026-10-08) sans devoir deviner quel AUTRE
 * rôle en est le titulaire légitime — une exclusion ciblée sur ce libellé,
 * pas une liste d'autorisation positive.
 */
export const DG_ROLES = ['DG', 'Direction Générale', 'Recteur', 'Président', 'Vice-Chancelier', 'Provost', 'Principal'];

/**
 * Rôles de gestion de scolarité.
 * Remplace les comparaisons directes à 'Gestionnaire Scolarité'.
 */
export const SCOLARITE_ROLES = ['Gestionnaire Scolarité', 'Agent Comptable'];

/**
 * Rôles Registraire et assimilés (assistant registraire, scolarité).
 * Permet au Doyen / Directeur et à la gouvernance de dénombrer précisément
 * les effectifs du bureau du registraire.
 */
export const REGISTRAR_ROLES = [
  'Registraire',
  'Registraire Institutionnel',
  'Bureau du Registraire',
  'Assistant Registraire',
  'Assistante Registraire',
  'Adjoint Registraire',
  'Adjointe Registraire',
  'Gestionnaire Scolarité',
  'Responsable Admissions',
];

/**
 * Membres du personnel académique ou de direction dont le rôle inclut ou implique l'enseignement.
 *
 * 'Directeur des écoles' (avec « Directeur ») n'est PAS une valeur de l'enum
 * `user_role` en base — seule 'Direction des écoles' existe. Tout
 * `.in('role', ...)` sur `profiles` avec le libellé fautif échoue en entier
 * (« invalid input value for enum user_role », PostgREST 22P02) : constaté
 * sur ContenuFormationView.jsx ("Impossible de charger les données
 * académiques", signalé en production, 2026-10-10). Ne jamais le
 * réintroduire ici.
 */
export const ACADEMIC_STAFF_ROLES = [
  'Responsable Académique',
  'Responsable de Filière',
  'Directeur Académique',
  'Direction des écoles',
  "Directeur d'École",
  'Directeur Ecole',
  'Directeur',
  'Doyen',
  'Chef de Département',
  'Chargé Académique',
  'Coordonnateur de Cycle',
  'Coordonnateur de Programme',
  'Coordonnateur de Niveau',
  'Direction Générale',
  'DG',
  'Gestionnaire Scolarité',
  // Variantes "registraire" regroupées avec Gestionnaire Scolarité dans
  // portalNavigation.js (resp-scol-dashboard) comme un seul et même poste —
  // ajoutées ici pour la même raison (signalé par Alfred, 2026-10-08 :
  // "plutôt registraire", le libellé réel du profil peut différer de
  // 'Gestionnaire Scolarité' sans changer la fonction).
  'Registraire',
  'Registraire Institutionnel',
  'Bureau du Registraire',
  'Responsable Admissions',
];

export const TEACHER_ROLES = [
  'Enseignant',
  'Enseignant Permanent',
  'Enseignant Associé',
  'Vacataire',
  'Tuteur',
  'Encadreur',
];

export const STUDENT_ROLES = [
  'Apprenant',
  'Étudiant',
];

/**
 * Rôles de direction/coordination académique qui sont AUSSI des enseignants
 * permanents, même compte (pas de second compte enseignant à créer) :
 * Responsable Académique, les 3 Coordonnateurs, Doyen (confirmés par Alfred,
 * migration 20261009130000, question posée explicitement pour fixer cette
 * liste) + les 4 libellés qui sont le MÊME poste que Doyen sous un autre nom
 * (Directeur, Directeur d'École, Direction des écoles, Directeur Ecole —
 * confirmé par `roleLabels.js::estChefDEcole`, seule source qui distingue un
 * synonyme de libellé d'un poste différent). « Chargé Académique » en est
 * explicitement exclu (poste d'assistanat, aucune charge de cours).
 *
 * « Directeur Académique » et « Chef de Département » retirés le 2026-10-11
 * (audit QA) : ils partagent le même rôle système RBAC `directeur` que Doyen
 * dans useGovAccess.js (accès portail/menu), mais ce n'est PAS la même
 * question que « confirmé enseignant permanent pour la paie/examens » —
 * `estChefDEcole()` les exclut explicitement du même poste que Doyen, et la
 * question précise a été posée et répondue par Alfred le 2026-10-09 sans les
 * inclure. Même rôle RBAC ne veut pas dire même statut d'enseignant.
 *
 * Tout flux qui dérive "qui est enseignant" à partir de `profiles.role` doit
 * inclure ce groupe en plus de TEACHER_ROLES, sous peine de recréer le même
 * trou (annuaire fn_sync_directory_person_from_profile, migration
 * 20261009130000 ; computeAvailableRoles, useAuth.jsx).
 */
export const TEACHER_DOUBLE_HAT_ROLES = [
  'Responsable Académique',
  'Coordonnateur de Cycle',
  'Coordonnateur de Programme',
  'Coordonnateur de Niveau',
  'Doyen',
  'Direction des écoles',
  "Directeur d'École",
  'Directeur Ecole',
  'Directeur',
];

export const ADMIN_ROLES = [
  'Master', 'IT',
  'DG', 'Direction Générale', 'Recteur', 'Président',
  'Vice-Chancelier', 'Provost', 'Principal',
  'Administrateur',
];

export const MANAGER_ROLES = [
  'Directeur Académique', 'Direction des écoles',
  "Doyen", "Directeur d'École", 'Directeur Ecole', 'Directeur',
  'Chef de Département',
  'Coordonnateur de Programme', 'Coordonnateur de Cycle', 'Coordonnateur de Niveau',
  'Responsable Académique', 'Responsable de Filière', 'Référent de Spécialité',
  'Chargé Académique', 'Responsable Qualité', 'Responsable Recherche',
  'Responsable Développement', 'Manager Qualité',
  'Gestionnaire Scolarité', 'Agent Comptable',
];

/**
 * Enseignants vacataires / temps partiel.
 * Sous-ensemble de TEACHER_ROLES pour les statistiques de ventilation.
 */
export const PART_TIME_TEACHER_ROLES = [
  'Vacataire',
  'Tuteur',
  'Encadreur',
];

/**
 * Rôles spéciaux: anciens diplômés, partenaires.
 * Ces rôles n'ont pas de droits système — affichage uniquement.
 */
export const ALUMNI_ROLES   = ['Alumni'];
export const PARTNER_ROLES  = ['Partenaire'];

/**
 * Personnel administratif et de support (hors enseignants et managers).
 *
 * Seul 'Comptable' est une valeur réelle de l'enum `user_role` en base
 * (vérifié 2026-10-12). 'Secrétariat', 'Secrétaire de Direction',
 * "Agent d'Accueil", 'Technicien', 'Bibliothécaire' et 'Caissier' n'en font
 * PAS partie — même défaut que celui déjà documenté sur ACADEMIC_STAFF_ROLES
 * plus haut ('Directeur des écoles') : un `.in('role', ...)` qui les inclut
 * échoue EN ENTIER (« invalid input value for enum user_role », PostgREST
 * 400), constaté sur CollaborateursView.jsx (/dashboard/staff, compteurs
 * TOTAL/ACTIVE à 0 — la requête `profiles` échouait avant même de lire une
 * ligne). Aucun profil ne peut porter ces libellés (la colonne est un enum
 * strict) : ils étaient déjà morts pour tout usage JS local.
 */
export const STAFF_ROLES = [
  'Comptable',
];
